import {
  faInfoCircle,
  faPlus,
  faTrashAlt,
} from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFieldArray, useFormState } from "react-hook-form";

import { Button } from "src/base-components/Button";
import { PredefinedValueEditor } from "src/base-components/schemaKit/PredefinedValueEditor";
import {
  SortableField,
  SortableFieldsContext,
} from "src/base-components/schemaKit/SortableFields";
import { getPredefinedValueUniqueValidator } from "src/base-components/schemaKit/validators";
import { EnumColor } from "src/clients/features-control/api";
import { IconButton } from "src/design-system/Icon";
import { EventSchemaForm } from "src/schema/schemaEditor/types";

export const EnumValuesEditor = ({ property }: { property: string }) => {
  const { fields, append, remove, move } = useFieldArray<EventSchemaForm>({
    name: `properties.${property}.enum_schema.values`,
  });

  const handleChangeOrder = ({
    oldIndex,
    newIndex,
  }: {
    oldIndex: number;
    newIndex: number;
  }) => {
    move(oldIndex, newIndex);
  };

  return (
    <div className="ml-2.5 flex flex-col gap-y-3 border-gray-200 border-l pl-4">
      <SortableFieldsContext
        items={fields.map((field) => field.id)}
        onChangeOrder={handleChangeOrder}
      >
        {fields.map((field, index) => (
          <Controller<
            EventSchemaForm,
            `properties.${string}.enum_schema.values.${number}`
          >
            key={field.id}
            name={`properties.${property}.enum_schema.values.${index}`}
            render={({ field: controllerField }) => (
              <SortableField id={field.id}>
                <EnumValueField
                  immutable={!controllerField.value?._isNew}
                  index={index}
                  property={property}
                  onRemove={remove}
                />
              </SortableField>
            )}
          />
        ))}
      </SortableFieldsContext>
      <div>
        <Button
          iconLeft={faPlus}
          size="sm"
          variant="secondary"
          onClick={() =>
            append(
              {
                color: EnumColor.GRAY,
                value: "",
                _isNew: true,
              },
              {
                focusName: `properties.${property}.enum_schema.values.${fields.length}.value`,
              },
            )
          }
        >
          Add value
        </Button>
      </div>
    </div>
  );
};

const EnumValueField = ({
  property,
  index,
  immutable,
  onRemove,
}: {
  property: string;
  index: number;
  immutable: boolean;
  onRemove: (index: number) => void;
}) => {
  const formState = useFormState<EventSchemaForm>();

  return (
    <div className="flex items-center gap-x-2">
      <div className="flex-1">
        <PredefinedValueEditor
          colorField={{
            name: `properties.${property}.enum_schema.values.${index}.color`,
          }}
          dataLoc="enum-field"
          disabled={immutable ? ["value"] : false}
          error={
            formState.errors.properties?.[property]?.enum_schema?.values?.[
              index
            ]?.value?.message
          }
          suffixIcon={
            !immutable
              ? {
                  icon: faInfoCircle,
                  helpText: "Newly added values cannot be changed once saved.",
                }
              : undefined
          }
          valueField={{
            name: `properties.${property}.enum_schema.values.${index}.value`,
            rules: {
              required: "Value is required",
              validate: {
                unique: getPredefinedValueUniqueValidator(
                  `properties.${property}.enum_schema.values`,
                ),
              },
            },
          }}
        />
      </div>
      <div className="pt-1">
        {!immutable && (
          <IconButton
            dataLoc="enum-field-delete"
            icon={faTrashAlt}
            size="xs"
            onClick={() => onRemove(index)}
          />
        )}
      </div>
    </div>
  );
};
